import { useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AuthContext } from '@urbanx/agx.auth.client'
import dayjs from 'dayjs'
import { ContentLayout, PageLayout } from 'layout'
import { AgxResponsiveTable } from '@urbanx/agx-ui-components'
import {
  AgxTextInput,
  AgxTypography,
  AgxDropdown,
  AgxRow,
  AgxTab,
  TabOption,
  AgxColumn,
  AgxCheckbox,
} from '@urbanx/agx.ui-components2'
import {
  getContractCommissionDiscrepancies,
  getListingDiscrepancies,
  IDiscrepancyRequest,
} from 'services/functions/staff/getDiscrepancies'
import useCommissionDiscrepanciesTableConfig from './useCommissionDiscrepanciesTableConfig'
import useListingDiscrepanciesTableConfig from './useListingDiscrepanciesTableConfig'
import { ICommissionDiscrepancy, IListingDiscrepancy } from 'types/rex'
import './discrepancies.scss'

const Discrepancies = () => {
  const discrepancies = 'discrepancies'
  const { getAuthToken } = useContext(AuthContext)
  const TABS: TabOption[] = [
    { id: 'Listings', label: 'Listings', selected: true },
    { id: 'Contracts', label: 'Contracts', selected: false },
  ]
  const [selectedTab, setSelectedTab] = useState<TabOption>(
    TABS.find((t) => t.selected) ?? TABS[0]
  )
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('')

  const [discrepanciesRequest, setDiscrepancyRequest] =
    useState<IDiscrepancyRequest>({
      periodStart: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      periodEnd: undefined,
      filterByAnniversaryDate: false,
    })

  // Setup the listing discrepancies ==================================
  const listingsKey = `listing-${discrepancies}`
  const { data: listingDiscrepancies, isPending: listingsArePending } =
    useQuery<IListingDiscrepancy[] | undefined>({
      queryKey: [listingsKey, discrepanciesRequest],
      queryFn: () =>
        getListingDiscrepancies(discrepanciesRequest, getAuthToken),
    })
  const filteredListingDiscrepancies = useMemo(() => {
    if (!listingDiscrepancies) return []
    if (!selectedAgencyId) return listingDiscrepancies
    return listingDiscrepancies.filter((d) => d.agencyId === selectedAgencyId)
  }, [listingDiscrepancies, selectedAgencyId])

  const [filteredListingTableConfig, filteredListingRows] =
    useListingDiscrepanciesTableConfig(filteredListingDiscrepancies)
  // ==================================================================

  // Setup the commission discrepancies ================================
  const commisionsKey = `commission-${discrepancies}`
  const { data: commissionDiscrepancies, isPending: commissionsArePending } =
    useQuery<ICommissionDiscrepancy[] | undefined>({
      queryKey: [commisionsKey, discrepanciesRequest],
      queryFn: () =>
        getContractCommissionDiscrepancies(discrepanciesRequest, getAuthToken),
    })
  const filteredCommissionDiscrepancies = useMemo(() => {
    if (!commissionDiscrepancies) return []
    if (!selectedAgencyId) return commissionDiscrepancies
    return commissionDiscrepancies.filter(
      (d) => d.agencyId === selectedAgencyId
    )
  }, [commissionDiscrepancies, selectedAgencyId])

  const [filteredCommissionTableConfig, filteredCommissionRows] =
    useCommissionDiscrepanciesTableConfig(filteredCommissionDiscrepancies)
  // ==================================================================

  useEffect(() => {
    setSelectedAgencyId('')
  }, [selectedTab])

  const agenciesFilter = useMemo(() => {
    const uniqueAgencies = new Map<string, { label: string; value: string }>()

    uniqueAgencies.set('', { label: 'All Agencies', value: '' })

    if (selectedTab.id === TABS[0].id) {
      listingDiscrepancies?.forEach((discrepancy) => {
        if (discrepancy.agencyId && discrepancy.agencyName) {
          uniqueAgencies.set(discrepancy.agencyId, {
            label: discrepancy.agencyName,
            value: discrepancy.agencyId,
          })
        }
      })
    } else if (selectedTab.id === TABS[1].id) {
      commissionDiscrepancies?.forEach((discrepancy) => {
        if (discrepancy.agencyId && discrepancy.agencyName) {
          uniqueAgencies.set(discrepancy.agencyId, {
            label: discrepancy.agencyName,
            value: discrepancy.agencyId,
          })
        }
      })
    }

    return Array.from(uniqueAgencies.values())
  }, [selectedTab, listingDiscrepancies, commissionDiscrepancies])

  useEffect(() => {
    if (discrepanciesRequest.periodStart) {
      getContractCommissionDiscrepancies(discrepanciesRequest, getAuthToken)
    }
  }, [discrepanciesRequest, getAuthToken])

  const filterChanged = (id: string, value: string) => {
    switch (id) {
      case `${discrepancies}-date-start`:
        setDiscrepancyRequest((prev) => ({
          ...prev,
          periodStart: dayjs(value).format('YYYY-MM-DD'),
        }))
        break
      case `${discrepancies}-date-end`:
        setDiscrepancyRequest((prev) => ({
          ...prev,
          periodEnd: value ? dayjs(value).format('YYYY-MM-DD') : undefined,
        }))
        break
      case `${discrepancies}-anniversary-date`:
        setDiscrepancyRequest((prev) => ({
          ...prev,
          filterByAnniversaryDate: value === 'true',
        }))
        break
      default:
        console.warn(`Unhandled discrepancies filter change for id: ${id}`)
        return
    }
  }

  const formatDiscrepancyCount = (count: number | undefined) => {
    return count + (count === 1 ? ' discrepancy' : ' discrepancies')
  }

  const renderDiscrepancyTab = (
    tabName: string,
    data: any[] | undefined,
    tableConfig: any,
    tableRows: any,
    isLoading: boolean
  ) => (
    <ContentLayout key={`${discrepancies}-display`} isStaff>
      {isLoading ? (
        <AgxRow
          gap="spacing-lg"
          padding={{ top: 'spacing-2xl', bottom: 'spacing-2xl' }}
          alignItems="alignCenter"
          justifyContent="center"
          fill
        >
          <AgxTypography
            id={`${discrepancies}-loading`}
            type="title"
            size="large"
            opacityPercent={50}
          >
            Loading ... please be patient, this may take around 60 seonds.
          </AgxTypography>
        </AgxRow>
      ) : (
        <>
          <AgxRow gap="spacing-4xl" padding={{ top: 'spacing-2xl', bottom: 'spacing-2xl' }}>
            <AgxColumn gap="spacing-sm" padding={{ left: 'spacing-2xl' }}>
              <AgxTypography
                id={`${discrepancies}-summary`}
                type="heading"
                size="small"
              >
                {tabName}
              </AgxTypography>
              <AgxTypography
                id={`${discrepancies}-count`}
                type="caption"
                size="medium"
                opacityPercent={50}
              >
                {formatDiscrepancyCount(data?.length)}
              </AgxTypography>
            </AgxColumn>
          </AgxRow>
          <AgxRow gap="spacing-4xl" fill>
            <AgxResponsiveTable
              key={`${tabName}-${data}-table`}
              columnConfig={tableConfig}
              rowProps={tableRows}
              showModal={false}
            />
          </AgxRow>
        </>
      )}
    </ContentLayout>
  )

  return (
    <PageLayout
      agentName=""
      primaryPageTitle="Reporting"
      currentPageTitle="Discrepancies"
    >
      <AgxColumn justifyContent="space-between" gap="spacing-4xl">
        <AgxRow
          gap="spacing-4xl"
          alignItems="alignCenter"
          justifyContent="space-between"
          fill
          padding={{ top: 'spacing-xl', bottom: 'spacing-xl', left: 'spacing-xl', right: 'spacing-xl' }}
        >
          <AgxTab
            options={TABS.map((tab) => ({
              ...tab,
              selected: selectedTab.id === tab.id,
            }))}
            onChange={(tab) => setSelectedTab(tab)}
          />
          <AgxRow gap="spacing-2xl" alignItems="alignCenter">
            <AgxCheckbox
              id={`${discrepancies}-anniversary-date`}
              label="Filter by Anniversary Date"
              checked={discrepanciesRequest.filterByAnniversaryDate}
              onChange={(checked) =>
                filterChanged(
                  `${discrepancies}-anniversary-date`,
                  checked.toString()
                )
              }
            />
            <AgxTextInput
              id={`${discrepancies}-date-start`}
              type="date"
              hideOptionalText
              defaultValue={discrepanciesRequest.periodStart}
              disabled={discrepanciesRequest.filterByAnniversaryDate}
              onInputValueChange={({ id, value }) => filterChanged(id, value)}
            />
            <AgxTextInput
              id={`${discrepancies}-date-end`}
              type="date"
              defaultValue={discrepanciesRequest.periodEnd}
              hideOptionalText
              onInputValueChange={({ id, value }) => filterChanged(id, value)}
            />
            <AgxDropdown
              id={`${discrepancies}-filter-select`}
              options={agenciesFilter}
              hideOptionalText
              onChange={(value) => {
                setSelectedAgencyId(value)
              }}
              selected={selectedAgencyId}
              placeholder="Agencies"
            />
          </AgxRow>
        </AgxRow>
        {selectedTab.id === TABS[0].id &&
          renderDiscrepancyTab(
            selectedTab.id,
            filteredListingDiscrepancies,
            filteredListingTableConfig,
            filteredListingRows,
            listingsArePending
          )}
        {selectedTab.id === TABS[1].id &&
          renderDiscrepancyTab(
            selectedTab.id,
            filteredCommissionDiscrepancies,
            filteredCommissionTableConfig,
            filteredCommissionRows,
            commissionsArePending
          )}
      </AgxColumn>
    </PageLayout>
  )
}

export default Discrepancies
